import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { m } from 'framer-motion';
import { useState, useTransition } from 'react';
import Iconify from 'src/components/iconify';

type SegmentedTab<Value extends string> = {
  value: Value;
  label: string;
  icon: string;
};

type SegmentedTabsProps<Value extends string> = {
  ariaLabel: string;
  currentTab: Value;
  tabs: Array<SegmentedTab<Value>>;
  onChangeTab: (tab: Value) => void;
  disabled?: boolean;
  animationMode?: 'spring' | 'css';
  deferContent?: boolean;
};

export default function SegmentedTabs<Value extends string>({
  ariaLabel,
  currentTab,
  tabs,
  onChangeTab,
  disabled = false,
  animationMode = 'spring',
  deferContent = false,
}: SegmentedTabsProps<Value>) {
  const [pendingTab, setPendingTab] = useState(currentTab);
  const [isPending, startTransition] = useTransition();
  // Only this small selector updates urgently; keep the parent panel out of that commit.
  const selectedTab = deferContent && isPending ? pendingTab : currentTab;
  const activeTabIndex = tabs.findIndex((tab) => tab.value === selectedTab);

  return (
    <Box
      role="tablist"
      aria-label={ariaLabel}
      aria-busy={deferContent && isPending}
      sx={(theme) => ({
        position: 'relative',
        display: 'inline-grid',
        gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`,
        width: { xs: '100%', sm: 'auto' },
        p: 0.35,
        mb: 2,
        overflow: 'hidden',
        borderRadius: 1,
        border: '1px solid',
        borderColor: alpha(theme.palette.grey[500], 0.18),
        backgroundColor: 'transparent',
      })}
    >
      <Box
        component={animationMode === 'css' ? 'span' : m.span}
        aria-hidden
        {...(animationMode === 'spring' && {
          animate: { x: `${Math.max(activeTabIndex, 0) * 100}%` },
          transition: { type: 'spring', stiffness: 480, damping: 36 },
        })}
        sx={(theme) => ({
          position: 'absolute',
          ...(animationMode === 'css' && {
            transform: `translateX(${Math.max(activeTabIndex, 0) * 100}%)`,
            transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
            willChange: 'transform',
          }),
          zIndex: 0,
          top: 4,
          bottom: 4,
          left: 4,
          width: `calc((100% - 8px) / ${tabs.length})`,
          overflow: 'hidden',
          borderRadius: 0.75,
          boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(
            100deg,
            ${alpha(theme.palette.primary.main, 0.15)} 0%,
            ${alpha(theme.palette.primary.main, 0.25)} 44%,
            ${alpha(theme.palette.primary.main, 0.15)} 100%
          )`,
          backgroundSize: '220% 100%',
          animation:
            animationMode === 'css'
              ? 'none'
              : 'segmentedTabActiveColorFlow 4s ease-in-out infinite alternate',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            opacity: 0.8,
            transform: 'translateX(-115%)',
            background: `linear-gradient(
              105deg,
              transparent 0%,
              transparent 30%,
              ${alpha(theme.palette.common.white, 0.16)} 42%,
              ${alpha(theme.palette.common.white, 0.55)} 50%,
              ${alpha(theme.palette.common.white, 0.16)} 58%,
              transparent 70%,
              transparent 100%
            )`,
            animation: 'segmentedTabReflection 2.8s ease-in-out infinite',
          },
          '@keyframes segmentedTabActiveColorFlow': {
            from: { backgroundPosition: '0% 50%' },
            to: { backgroundPosition: '100% 50%' },
          },
          '@keyframes segmentedTabReflection': {
            '0%': { transform: 'translateX(-115%)' },
            '45%, 100%': { transform: 'translateX(115%)' },
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            animation: 'none',
            '&::before': { animation: 'none' },
          },
        })}
      />

      {tabs.map((tab) => {
        const selected = selectedTab === tab.value;

        return (
          <ButtonBase
            key={tab.value}
            role="tab"
            aria-selected={selected}
            disabled={disabled}
            onClick={() => {
              if (deferContent) {
                setPendingTab(tab.value);
                startTransition(() => onChangeTab(tab.value));
              } else onChangeTab(tab.value);
            }}
            sx={(theme) => ({
              position: 'relative',
              zIndex: 1,
              minHeight: 38,
              minWidth: { xs: 0, sm: 128 },
              justifyContent: 'center',
              gap: { xs: 0.5, sm: 0.75 },
              px: { xs: 0.5, sm: 1 },
              borderRadius: 0.75,
              bgcolor: 'transparent',
              color: selected ? 'primary.main' : 'text.secondary',
              opacity: disabled ? 0.55 : 1,
              transition: theme.transitions.create(['color'], {
                duration: theme.transitions.duration.shorter,
              }),
              '&:hover': {
                color: selected ? 'primary.main' : 'text.primary',
                backgroundColor: 'transparent',
              },
            })}
          >
            <Box
              sx={(theme) => ({
                position: 'relative',
                width: { xs: 18, sm: 22 },
                height: { xs: 18, sm: 22 },
                flexShrink: 0,
                borderRadius: 0.65,
                display: 'grid',
                placeItems: 'center',
                color: selected ? 'primary.main' : 'text.secondary',
                backgroundColor: selected
                  ? alpha(theme.palette.primary.main, 0.08)
                  : alpha(theme.palette.grey[500], 0.06),
              })}
            >
              <Iconify icon={tab.icon} sx={{ width: 14, height: 14 }} />
            </Box>

            <Typography
              variant="caption"
              sx={{
                position: 'relative',
                fontWeight: selected ? 800 : 600,
                color: selected ? 'primary.main' : 'text.primary',
                fontSize: { xs: 12, sm: 14 },
                lineHeight: 1,
              }}
            >
              {tab.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
